import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Button from '../../common/components/button';
import Div from '../../common/components/div';
import H1 from '../../common/components/h1';
import MedicareNumberDialog from '../../common/components/MedicareNumberDialog';
import StackingButtons from '../../common/components/StackingButtons';
import TranslatedSpan from '../../common/components/TranslatedSpan';
import capitalizeFirstLetter from '../../common/functions/capitalizeFirstLetter';
import { isValidMedicareNumber } from '../../common/functions/isValidMedicareNumber';
import { isValidYear } from '../../common/functions/isValidYear';
import translate from '../../common/functions/translate';
import { useConversion, useFormStart } from '../../common/hooks/use.analytics';
import dateFieldValue, { DateObject } from '../../common/objects/models/date.field.value';
import LastNameSection from '../../common/components/LastNameSection';
import MedicareNumberSection from '../../common/components/MedicareNumberSection';
import CancelButton from '../../common/components/CancelButton';
import { AllFieldsRequired } from '../../common/components/AllFieldsRequired';
import useVerifyAccountApi, { VerifyAccountResponse } from '../../common/hooks/useVerifyAccountApi';
import { AxiosResponse } from 'axios';
import { FindMyAccountFields, SmartErrorAlert } from '../../common/components/SmartErrorAlert';
import { InvalidField } from '../../common/functions/errorAlertContent/getAlertContent';
import { DateFields2 } from '../../common/components/DateFields2';

export interface InitData {
    dateOfBirth?: DateObject
    lastName?: string
    medicareNumber?: string
    suffix?: string
}
interface EnterUserInfoSectionProps {
    initData?: InitData
    showPageCallBack: (num: number) => void
    setUserName: (name: string) => void
    setCreatedAt: (val: number) => void
}

type FieldErrState = 'empty' | 'invalid' | null;

export default function EnterUserInfoSection(props: EnterUserInfoSectionProps) {
    const initData: InitData = props.initData || {};

    const [lastNameRef, setLastNameRef] = useState<HTMLElement | null>(null);
    const [medicareNumberRef, setMedicareNumberRef] = useState<HTMLElement | null>(null);
    const [monthRef, setMonthRef] = useState<HTMLElement | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [dateOfBirth, setDateOfBirth] = useState(initData.dateOfBirth || dateFieldValue());
    const [dialogIsOpen, setDialogIsOpen] = useState(false);
    const findAccountApi = useVerifyAccountApi();
    const history = useHistory();
    const [lastName, setLastName] = useState(initData.lastName || '');
    const [medicareNumber, setMedicareNumber] = useState(initData.medicareNumber || '');
    const [suffix, setSuffix] = useState(initData.suffix || '');

    const formStart = useFormStart('account_found');
    const conversion = useConversion('account_found', 'account_found');

    const [apiResponse, setApiResponse] = useState<AxiosResponse<VerifyAccountResponse> | null>(null);

    const [invalidFields, setInvalidFields] = useState<InvalidField<FindMyAccountFields>[]>([]);

    const [mbiErrState, setMbiErrState] = useState<FieldErrState>(null);
    const [lastNameErrState, setLastNameErrState] = useState<FieldErrState>(null);
    const [dateOfBirthErrState, setDateOfBirthErrState] = useState<FieldErrState>(null);

    const [mbiTouched, setMbiTouched] = useState(false);
    const [lastNameTouched, setLastNameTouched] = useState(false);
    const [dateOfBirthTouched, setDateOfBirthTouched] = useState(false);

    const [submitAttempted, setSubmitAttempted] = useState(false);


    const getFindMyAccountButton = () => {
        return <>
            <Button
                variation={'solid'}
                onClick={handleFindAccountButtonClick}
                isLoading={isLoading}
            >
                <TranslatedSpan english={'find_my_account'}/>
            </Button>
        </>;
    };

    const handleFindAccountResponse = (response: AxiosResponse<VerifyAccountResponse> | null) => {
        setApiResponse(response);
        if (!response) {
            return;
        }
        const { status, data } = response;
        if (status === 200) {
            conversion.success();
            props.setUserName(data.username);
            props.setCreatedAt(data.createdAt);
            props.showPageCallBack(1);
        } else if (status === 404) {
            // give time for smart alert to fire analytics event before page unmounts
            setTimeout(() => {
                props.showPageCallBack(2);
            }, 10);
        }
    }

    const handleFindAccountButtonClick = async () => {
        setSubmitAttempted(true);
        if (isLoading) {
            return;
        }
        setIsLoading(true);
        conversion.attempt();

        const newInvalidFields: InvalidField<FindMyAccountFields>[] = [];
        if (mbiErrState) {
            newInvalidFields.push({ name: 'mbi', type: mbiErrState });
        }
        if (lastNameErrState) {
            newInvalidFields.push({ name: 'lastName', type: lastNameErrState });
        }
        if (dateOfBirthErrState) {
            newInvalidFields.push({ name: 'dateOfBirth', type: dateOfBirthErrState });
        }

        setInvalidFields(newInvalidFields);
        if (newInvalidFields.length) {
            setIsLoading(false);
            return;
        }


        let lastNameVal = lastName;
        if (suffix) {
            lastNameVal += ' ' + suffix;
        }

        setIsLoading(false);
        setApiResponse(null);
        handleFindAccountResponse(await findAccountApi.post(medicareNumber, lastNameVal, dateOfBirth));
    };

    useEffect(() => {
        const { month, day, year } = dateOfBirth;
        const isEmpty = !month && !day && !year;
        const isInvalid = !isValidYear(year);

        if (isEmpty) {
            setDateOfBirthErrState('empty');
        } else if (isInvalid) {
            setDateOfBirthErrState('invalid');
        } else {
            setDateOfBirthErrState(null);
        }
    }, [dateOfBirth]);

    const updateDateOfBirth = (dateObject: DateObject) => {
        formStart();
        setDateOfBirth(dateObject);
    };

    useEffect(() => {
        setLastNameErrState(lastName.length ? null : 'empty');
    }, [lastName]);

    const updateLastName = (value: string) => {
        formStart();
        const newValue = value.trimStart();
        setLastName(newValue);
    };

    useEffect(() => {
        const isValid = isValidMedicareNumber(medicareNumber);
        const isEmpty = !medicareNumber;
        if (isValid) {
            setMbiErrState(null);
        } else if (isEmpty) {
            setMbiErrState('empty');
        } else {
            setMbiErrState('invalid');
        }
    }, [medicareNumber]);

    const updateMedicareNumber = (value: string) => {
        formStart();
        const newValue = value.trim();
        setMedicareNumber(newValue);
    };


    const getMedicareErrorMessage = () => {
        if (!mbiTouched && !submitAttempted) {
            return null;
        }
        if (mbiErrState === 'empty') {
            return translate('enter_your_medicare_number');
        }
        if (mbiErrState === 'invalid') {
            return translate('check_mbi_and_try_again')
        }
        return null;
    }

    const getLastNameErrorMessage = () => {
        if (!lastNameTouched && !submitAttempted) {
            return null;
        }
        return lastNameErrState ? translate('enter_your_last_name') : null;
    }

    const getDateOfBirthErrMessage = () => {
        if (!dateOfBirthTouched && !submitAttempted) {
            return null;
        }
        if (dateOfBirthErrState === 'empty') {
            return translate('enter_your_dob');
        }
        if (dateOfBirthErrState === 'invalid') {
            return translate('check_dob_try_again');
        }
        return null;
    };

    const onInvalidFields = (fields: InvalidField<FindMyAccountFields>[]) => {
        fields.forEach(f => {
            if (f.name === 'mbi') {
                setMbiErrState('invalid');
            }
            if (f.name === 'lastName') {
                setLastNameErrState('invalid');
            }
            if (f.name === 'dateOfBirth') {
                setDateOfBirthErrState('invalid');
            }
        });
    }
    const header = translate('find_my_account');

    return <>
        <MedicareNumberDialog
            isOpen={dialogIsOpen}
            onExit={() => setDialogIsOpen(false)}
        />
        <Div
            isContainer={true}
            paddingySM={1} paddingyMD={2} paddingyLG={3} paddingyXL={4}
        >
            <fieldset style={{
                border: 0,
                margin: 0,
                padding: 0,
            }}>
                <Div isRow>
                    <Div cols={12}>
                        <H1 className='ds-u-padding-bottom--2'>
                            {capitalizeFirstLetter(translate('find_my_account').toLowerCase())}
                        </H1>
                        <legend>
                            <Div>
                                <TranslatedSpan
                                    className={'.ds-text'}
                                    style={{
                                        fontSize: 16,
                                        fontWeight: 400
                                    }}
                                    english={'check_if_you_have_medicare_account'}
                                />
                            </Div>
                        </legend>
                        <Div isRow className="ds-u-padding-top--3">
                            <Div cols={12}>
                                <AllFieldsRequired />
                                <SmartErrorAlert
                                    name="FindMyAccount"
                                    conversion={conversion}
                                    apiResponse={apiResponse}
                                    mbiRef={medicareNumberRef}
                                    lastNameRef={lastNameRef}
                                    dateOfBirthRef={monthRef}
                                    invalidFields={invalidFields}
                                    onInvalidField={onInvalidFields}
                                    parentHeading={header}
                                />
                            </Div>
                        </Div>
                    </Div>
                </Div>
                <Div isRow>
                    <Div cols={12} colsMD={8}>
                        <MedicareNumberSection
                            errorMessage={getMedicareErrorMessage()}
                            inputRef={setMedicareNumberRef}
                            onBlur={() => setMbiTouched(true)}
                            onChange={updateMedicareNumber}
                            onClick={formStart}
                            onFocus={formStart}
                            value={medicareNumber}
                            parentComponentHeading={header}
                        />
                    </Div>
                </Div>
                <LastNameSection
                    errorMessage={getLastNameErrorMessage()}
                    inputRef={setLastNameRef}
                    lastNameVal={lastName}
                    onLastNameBlur={() => setLastNameTouched(true)}
                    onLastNameChange={updateLastName}
                    onLastNameClick={formStart}
                    onSuffixChange={setSuffix}
                    suffixVal={suffix}
                />
                <Div isRow>
                    <Div cols={12}>
                        <DateFields2
                            date={dateOfBirth}
                            hint={translate('use_format_mmddyyyy')}
                            label={translate('date_of_birth')}
                            monthFieldRef={setMonthRef}
                            onComponentBlur={() => setDateOfBirthTouched(true)}
                            onChange={updateDateOfBirth}
                            errorMessage={getDateOfBirthErrMessage()}
                        />
                    </Div>
                </Div>
            </fieldset>
            <StackingButtons
                button1={<CancelButton onClick={() => history.push('/login')} />}
                button2={getFindMyAccountButton()}
            />
        </Div>
    </>;
}
